import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';
import { hasSubscriptions, type Product } from '@/domain/Product';
import dispatchTrackingEvent, {
  dispatchNullEcommerceObject,
  productToGtmItemListPayload,
  productToGtmProductPayloadEvent,
  subscriptionToGtmSubscriptionPayloadEvent,
  type Ecommerce,
  type StructuredGtmPayload
} from '@/widgets/services/TrackingService.ts';

export type GtmToggleFavorites = Omit<StructuredGtmPayload, 'action_type'> & {
  subscription_product_included: string;
  discovery_offer_included: string;
  click_location: string;
  item_list_name?: string;
  item_list_id?: string;
  item_id_event: string;
  item_name_event: string;
  item_category_event: string;
  item_range_event: string;
  item_technology_event: string;
  item_type_event: string;
  item_subscription_name_event?: string;
  item_subscription_category_event?: string;
} & { ecommerce: Ecommerce };

export const RAISED_BY = 'global_plp_v3';

const getAromaticsForTracking = (product: Product): string | undefined =>
  Array.isArray(product?.specifications?.aromatics) &&
  product?.specifications?.aromatics !== undefined
    ? product.specifications.aromatics
        .map((aromatic) => aromatic.title)
        .join(', ')
    : undefined;

const getIntensityForTracking = (product: Product): number | undefined =>
  product?.specifications?.intensity !== undefined
    ? (product.specifications.intensity as any).value
    : undefined;

const getCupSizeForTracking = (product: Product): string | undefined =>
  Array.isArray(product?.specifications?.cupSizes) &&
  product?.specifications?.cupSizes !== undefined
    ? product.specifications.cupSizes.map((cupSize) => cupSize.title).join(', ')
    : undefined;

export function trackProductIsFavoriteToggledFrom(
  discoveryOfferBundleProducts: string[],
  itemListName: string,
  hasBeenFavorited: boolean,
  currency: string,
  product: Product,
  trackingPosition: number,
  from: 'plp' | 'plp-alternatives' | 'pdp'
): void {
  const isDiscoveryOffer = discoveryOfferBundleProducts.includes(product.id);

  dispatchNullEcommerceObject();

  const subscriptionProductIncluded = hasSubscriptions(product);

  let price;

  if (product.type === 'capsule') {
    price =
      product.pricing?.pricePerCapsule !== undefined
        ? (product.pricing?.pricePerCapsule ?? 0) /
          PRICE_REPRESENTATION_MULTIPLIER
        : product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER;
  } else {
    price = product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER;
  }

  const payload: GtmToggleFavorites = {
    event_raised_by: RAISED_BY,
    event: hasBeenFavorited ? 'add_to_wishlist' : 'remove_from_wishlist',
    click_location: 'plp',
    subscription_product_included: subscriptionProductIncluded
      ? 'true'
      : 'false',
    discovery_offer_included: isDiscoveryOffer.toString(),
    ...productToGtmProductPayloadEvent(product),
    item_subscription_name_event: product.subscriptionsInfo?.id ?? undefined,
    item_subscription_category_event:
      product.subscriptionsInfo?.type ?? undefined,
    ecommerce: {
      currency,
      value: price,
      items: [
        productToGtmItemListPayload(
          product,
          isDiscoveryOffer,
          trackingPosition,
          RAISED_BY,
          itemListName || RAISED_BY
        )
      ]
    }
  };

  const subscriptionData = subscriptionToGtmSubscriptionPayloadEvent(
    product.subscriptionsInfo,
    false
  );

  if (payload.ecommerce.items && payload.ecommerce.items[0]) {
    payload.ecommerce.items[0] = {
      ...payload.ecommerce.items[0],
      ...(subscriptionData as any)
    };
  }

  if (from === 'plp-alternatives') {
    payload.event_raised_by = RAISED_BY;
    payload.click_location = 'plp oos alternative';

    if (payload.ecommerce.items && payload.ecommerce.items[0]) {
      payload.ecommerce.items[0] = {
        ...payload.ecommerce.items[0],
        item_list_id: RAISED_BY,
        item_list_name: `global_plp_v3_oos_alternative - ${product.name}`
      };
    }
  }

  if (from === 'pdp') {
    payload.event_raised_by = 'pdp_hero';
    payload.click_location = 'pdp hero';

    if (payload.ecommerce.items && payload.ecommerce.items[0]) {
      payload.ecommerce.items[0] = {
        ...payload.ecommerce.items[0],
        item_list_id: undefined,
        item_list_name: undefined
      };
    }
  }

  // here we apply the comments on https://nestle.atlassian.net/browse/ITNP-161372?focusedCommentId=3517303 to avoid changing
  // the snapshots and existing tests. We just change the requested fields related to favorites
  payload.ecommerce.items = payload.ecommerce.items?.map((item) => ({
    ...item,
    item_added_by_user: undefined,
    price: (item as any).price,
    item_market_name: product?.name,
    quantity: 1
  }));

  payload.ecommerce.items = payload.ecommerce.items?.map((item) => ({
    ...item,
    coupon: undefined, // TODO
    discount: undefined, // TODO
    location_id: undefined, // TODO
    item_coffee_cup_size: getCupSizeForTracking(product),
    item_coffee_intensity: getIntensityForTracking(product),
    item_coffee_aromatic_profile: getAromaticsForTracking(product),
    item_selection_list: product?.productSelections?.join(',')
  }));

  // not needed on the docs https://nestle.atlassian.net/wiki/spaces/DIGMAN/pages/434701073/add_to_wishlist
  delete (payload as any).item_price_event;

  dispatchTrackingEvent(payload);
}
